import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  linkedSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterOutlet } from '@angular/router';
import {
  AuthApiActions,
  AuthBusinessProfilesPageActions,
  AuthFacade,
  AuthorizationConcept,
  AuthorizationService,
  AuthorizationType,
  AuthPageActions,
  AuthService,
  fromAuth
} from '@iot-platform/auth';
import { CachedUsersService, FeatureFlagsService, LocalStorageKeys, LocalStorageService, MediaWatcherService } from '@iot-platform/core';
import { BuildInfoComponent, ChatComponent, ResizeHandleComponent, SidenavModule } from '@iot-platform/iot-platform-ui';
import { BusinessProfile, Chat, ChatEvent, FeatureFlags, IotAction, UserAccount } from '@iot-platform/models/common';
import { SidenavMenuBlock } from '@iot-platform/models/i4b';
import { Store } from '@ngrx/store';
import { lastValueFrom, tap } from 'rxjs';
import { HeaderComponent } from '../components/header/header.component';
import { FeatureAppShellSettings } from '../models/app-shell-settings.model';
import { AbstractAppShellService } from '../services/abstract-app-shell.service';
import { AiChatBotService } from '../services/ai-chat-bot.service';
import { FEATURE_APP_SHELL_SETTINGS } from '../settings/app-shell-settings.provider';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'iot-platform-feature-app-shell',
  imports: [
    BuildInfoComponent,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    RouterOutlet,
    SidenavModule,
    MatTooltipModule,
    HeaderComponent,
    ChatComponent,
    ResizeHandleComponent
  ],
  templateUrl: './app-shell.component.html',
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'mat-mdc-select-bp-overlay-pane' }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureAppShellComponent implements OnInit {
  appTheme = 'default-theme';
  navigationTree: Signal<SidenavMenuBlock[]>;
  businessProfiles: Signal<BusinessProfile[]>;
  businessProfile: Signal<BusinessProfile>;
  currentUser: Signal<UserAccount>;
  currentUserLoading: Signal<boolean>;
  privileges: Signal<unknown>;
  isLoggedOut: Signal<boolean>;
  signOutPending: Signal<boolean>;
  opened: WritableSignal<boolean> = signal(true);
  authPending: Signal<boolean>;
  // Chatbot Spike
  chatBotOpen: WritableSignal<boolean> = signal(false);
  chat: Signal<Chat> = signal(null);
  sideNavContentWidth: WritableSignal<string> = signal('');
  drawerWidth: WritableSignal<string> = signal('');
  protected readonly appShellService: AbstractAppShellService = inject(AbstractAppShellService);
  protected readonly settings: FeatureAppShellSettings = inject(FEATURE_APP_SHELL_SETTINGS);
  protected readonly authService: AuthService = inject(AuthService);
  protected readonly authFacade: AuthFacade = inject(AuthFacade);
  protected readonly store: Store = inject(Store);
  protected readonly storage: LocalStorageService = inject(LocalStorageService);
  protected readonly cachedUsersService: CachedUsersService = inject(CachedUsersService);
  protected readonly injector: Injector = inject(Injector);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly mediaWatcherService: MediaWatcherService = inject(MediaWatcherService);
  isSmallScreen: Signal<boolean> = this.mediaWatcherService.isSmallScreen;
  isXSmallScreen: Signal<boolean> = this.mediaWatcherService.isXSmallScreen;
  protected readonly aiChatBotService: AiChatBotService = inject(AiChatBotService);
  protected readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  allowAiAssistant: Signal<boolean> = computed(() => this.featureFlagsService.isFeatureEnabled(FeatureFlags.AllowAiAssistant));
  protected readonly authorizationService: AuthorizationService = inject(AuthorizationService);
  canReadIOTAssistant: Signal<boolean> = computed(() => {
    this.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.IOT_ASSISTANT, AuthorizationType.READ);
  });
  // Routed content relies on the business-profile-id header + privileges being resolved first (see AuthInterceptor / AuthorizationService)
  // so we hold off rendering the <router-outlet> until both are ready, otherwise early API calls can fail or be unauthorized.
  // Latched: privileges are reset to null on every reload (business profile (re)selection), which must not destroy the routed page.
  contentReady: Signal<boolean> = linkedSignal<boolean, boolean>({
    source: () => !!this.privileges() && !!this.businessProfile()?.id,
    computation: (ready, previous) => ready || !!previous?.value
  });
  protected readonly router = inject(Router);

  constructor() {
    this.businessProfiles = this.store.selectSignal(fromAuth.selectBusinessProfilesForAccount);
    this.businessProfile = toSignal(this.appShellService.getCurrentBusinessProfile().pipe(takeUntilDestroyed(this.destroyRef)));
    this.currentUser = this.store.selectSignal(fromAuth.selectCurrentUser);
    this.currentUserLoading = this.store.selectSignal(fromAuth.selectAccountLoading);
    this.privileges = this.store.selectSignal(fromAuth.selectPrivileges);
    this.isLoggedOut = this.store.selectSignal(fromAuth.selectLoggedOut);
    this.signOutPending = this.store.selectSignal(fromAuth.selectSignOutPending);
    this.navigationTree = toSignal(this.appShellService.getNavigationTree().pipe(takeUntilDestroyed(this.destroyRef)));
    this.authPending = this.authFacade.authPending;

    this.opened.update(() => {
      if (this.isSmallScreen()) {
        return false;
      }
      if (this.appShellService.shouldCollapseSidenav()) {
        return false;
      }
      const isOpened = this.storage.get(LocalStorageKeys.STORAGE_SIDEBAR_OPENED_KEY);
      return isOpened ? isOpened === 'true' : true;
    });

    effect(() => {
      if (this.isSmallScreen()) {
        this.opened.set(false);
      }
    });

    effect(() => {
      if (this.settings.allowChatBot && this.allowAiAssistant() && this.canReadIOTAssistant()) {
        this.chat = this.aiChatBotService.chat;
      }
      this.chatBotOpen.set(false);
    });
  }

  ngOnInit(): void {
    this.handleBusinessProfileChangeEffect();
    this.handleBusinessProfileReadyEffect();
    this.handleRetrieveSessionEffect();
    this.loadUsersEffect();
  }

  logout(): void {
    this.store.dispatch(AuthPageActions.signOut());
  }

  returnHome(): void {
    this.appShellService.returnHome();
  }

  toggleSidenav(): void {
    this.opened.update((opened) => {
      opened = !opened;
      this.storage.set(LocalStorageKeys.STORAGE_SIDEBAR_OPENED_KEY, opened.toString());
      return opened;
    });
  }

  onChangeBusinessProfile(selected: BusinessProfile, withRedirect = false): void {
    this.chatBotOpen.set(false);
    this.storage.clearStorageOnBusinessProfileSelection();
    this.store.dispatch(
      AuthBusinessProfilesPageActions.selectBusinessProfile({
        selectedBusinessProfile: selected,
        withRedirect
      })
    );
  }

  openMyProfile(): void {
    this.appShellService.openMyProfile();
  }

  closePreferences(): void {
    this.opened.set(false);
    this.appShellService.closePreferences();
  }

  onChatBotClicked(): void {
    this.chatBotOpen.update((value) => !value);
  }

  refreshBusinessProfileList(): void {
    this.store.dispatch(AuthPageActions.loadAccount());
  }

  openReleaseNotes(): void {
    this.appShellService.openReleaseNotes();
  }

  onDispatchBotAction(event: ChatEvent): void {
    if (event.name === IotAction.CONFIGURE) {
      this.aiChatBotService.openCredentialsDialog();
    } else {
      this.aiChatBotService.addMessage(event, this.currentUser(), this.router.url);
    }
  }

  openChangelog(): void {
    this.appShellService.openChangelog();
  }

  private handleBusinessProfileReadyEffect() {
    effect(
      () => {
        const businessProfile = this.businessProfile();
        if (businessProfile?.id && businessProfile?.entityId) {
          this.store.dispatch(
            AuthBusinessProfilesPageActions.selectBusinessProfile({
              selectedBusinessProfile: businessProfile,
              withRedirect: false
            })
          );
          this.appShellService.onBusinessProfileReady(businessProfile);
        }
      },
      { injector: this.injector }
    );
  }

  private handleRetrieveSessionEffect() {
    effect(
      () => {
        const isLoggedOut = this.isLoggedOut();
        if (!isLoggedOut && this.authService.isLoggedInWithSSO()) {
          this.store.dispatch(AuthPageActions.retrieveSsoSession(this.authService.retrieveSsoTokens()));
        }
      },
      { injector: this.injector }
    );
  }

  private loadUsersEffect() {
    effect(
      () => {
        const currentUser = this.currentUser();
        if (currentUser) {
          this.cachedUsersService.loadUsers();
        } else {
          const signOutPending = this.signOutPending();
          if (this.authService.isLoggedInWithSSO() || signOutPending) {
            return;
          }
          const isLoggedOut = this.isLoggedOut();
          if (!isLoggedOut) {
            this.store.dispatch(AuthApiActions.retrieveSession());
          } else {
            this.storage.clear();
          }
        }
      },
      { injector: this.injector }
    );
  }

  private handleBusinessProfileChangeEffect() {
    effect(
      async () => {
        await lastValueFrom(
          this.authFacade.isBusinessProfileChanged$.pipe(
            tap((isBusinessProfileChanged: boolean) => {
              if (isBusinessProfileChanged) {
                this.appShellService.handleBusinessProfileChange();
              }
            })
          )
        );
      },
      { injector: this.injector }
    );
  }
}
